import { Button } from 'antd'
import { useTicketStepContext } from 'features/libra/ticket/TicketStateContext'
import { useTicketUpdate } from 'features/libra/ticket/TicketUpdateContext'
import {
  LibraWorflowStepComponentProps,
  UseValidateConfigHook,
} from 'features/libra/workflow/workflowStepsApi'
import sortBy from 'lodash/sortBy'
import sortedUniq from 'lodash/sortedUniq'
import { FC, useCallback, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { z } from 'zod'

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`

const StyledText = styled.span`
  font-size: 1.5rem;
  font-weight: bold;
`

export const HelloWorkflowStepWorldConfigSchema = z.object({
  name: z.string(),
})

type HelloWorkflowStepWorldProps = z.infer<
  typeof HelloWorkflowStepWorldConfigSchema
>

export const Component: FC<
  LibraWorflowStepComponentProps<HelloWorkflowStepWorldProps>
> = ({ config, setValidationResult }) => {
  const { ticketProps, updateTicketProps } = useTicketUpdate()
  const confirmedNames = ticketProps.confirmedNames as ReadonlyArray<string>
  const { isStepComplete, isActiveStep } = useTicketStepContext()

  const isConfirmed = useMemo(
    () => confirmedNames?.includes(config.name) ?? false,
    [confirmedNames, config.name]
  )
  useEffect(() => {
    setValidationResult(
      isConfirmed
        ? undefined
        : {
            status: 'Error',
            message: 'Step incomplete - click "Complete this step" to proceed',
          }
    )
  }, [isConfirmed, setValidationResult])

  const handleButtonClick = useCallback(() => {
    updateTicketProps({
      confirmedNames: sortedUniq(
        sortBy([...(confirmedNames ?? []), config.name])
      ),
    })
  }, [config.name, confirmedNames, updateTicketProps])

  return (
    <StyledContainer>
      <StyledText
        style={{
          color: isStepComplete ? 'green' : isActiveStep ? 'blue' : 'red',
        }}>
        Hello {config.name} World!
      </StyledText>
      {isActiveStep && (
        <Button onClick={() => handleButtonClick()}>Complete this step</Button>
      )}
    </StyledContainer>
  )
}

export const useValidateConfig: UseValidateConfigHook = ({ config }) => {
  return useMemo(
    () => HelloWorkflowStepWorldConfigSchema.safeParse(config).success,
    [config]
  )
}
